import React, { useEffect, useState } from "react";
import api from "../lib/auth";
import ReportingAdministrationPanel from "../components/ReportingAdministrationPanel";
import { Layers, RefreshCw, ShieldCheck } from "lucide-react";

export default function ReportingGroupsPage() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadGroups = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await api.get("/reporting-groups");
      const records = response?.data;

      if (!Array.isArray(records)) {
        throw new Error("Unexpected reporting groups response");
      }

      setGroups(records);
    } catch (err) {
      console.error("[ReportingGroups] Load failed", err);
      setError(
        err?.response?.status === 403
          ? "You do not have permission to view reporting groups."
          : "Unable to load reporting groups."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGroups();
  }, []);

  return (
    <div className="min-h-screen bg-[#09090b] text-white p-6 md:p-8">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-3">
              <Layers className="w-7 h-7 text-blue-400" />
              Reporting Groups
            </h1>
            <p className="text-zinc-400 mt-2">
              Configure consolidated operational reporting by location.
            </p>
          </div>

          <button
            onClick={loadGroups}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 rounded-lg
                       bg-zinc-800 hover:bg-zinc-700 disabled:opacity-50"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>

        <div className="rounded-xl border border-amber-800/50
                        bg-amber-950/20 p-4">
          <div className="flex items-center gap-2 text-amber-300 font-semibold">
            <ShieldCheck className="w-5 h-5" />
            Automatic Email Delivery Disabled
          </div>
          <p className="text-sm text-zinc-400 mt-2">
            Reporting groups are in configuration and testing mode.
            No client emails are automatically distributed.
          </p>
        </div>

        <ReportingAdministrationPanel groups={groups} onGroupsChanged={loadGroups} />

        <div className="rounded-xl border border-zinc-800
                        bg-[#18181b] overflow-hidden">
          <div className="p-5 border-b border-zinc-800">
            <h2 className="text-lg font-semibold">
              Existing Reporting Groups
            </h2>
          </div>

          {loading ? (
            <div className="p-8 text-zinc-400">
              Loading reporting groups...
            </div>
          ) : error ? (
            <div role="alert" className="p-8 text-red-400">
              {error}
            </div>
          ) : groups.length === 0 ? (
            <div className="p-10 text-center">
              <Layers className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
              <p className="font-medium">No reporting groups configured</p>
              <p className="text-zinc-500 text-sm mt-2">
                Use Sites & Groups above to create your first
                reporting group. Automatic delivery remains disabled.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-zinc-800">
              {groups.map((group) => (
                <div
                  key={group.id}
                  className="p-5 flex flex-wrap items-center
                             justify-between gap-4"
                >
                  <div>
                    <h3 className="font-semibold">
                      {group.name}
                    </h3>
                    <p className="text-sm text-zinc-400 mt-1">
                      {group.site_ids?.length || 0} locations
                    </p>
                  </div>

                  <div className="text-right text-sm">
                    <p className="text-zinc-300">
                      {group.report_settings?.mode || "consolidated"}
                    </p>
                    <p className="text-amber-400 mt-1">
                      Delivery disabled
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <p className="text-xs text-zinc-500">
          Reporting window: 07:00–07:00 Australia/Sydney.
          Scheduled delivery target: 08:00.
          Automatic distribution remains disabled.
        </p>
      </div>
    </div>
  );
}
